import React from 'react'
import { observer } from 'mobx-react'
import styles from './DocumentDetailsTab.sass'
import {
  FormRow,
  TextInputContainer,
  FormGroup,
  Form,
  TextAreaContainer,
} from 'components/inputs'
import {
  CustomSelect,
  ItemSource,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import { DocumentDetailsModalViewModel } from '../DocumentDetailsModalViewModel'
import ErrorBlock from 'components/ErrorBlock'

interface DocumentDetailsTabProps {
  vm: DocumentDetailsModalViewModel
}

export default observer(function DocumentDetailsTab({
  vm,
}: DocumentDetailsTabProps) {
  const itemSource = React.useMemo(
    () =>
      ItemSource.static(vm.documentTypes, (item, text) => {
        return item.name.toLowerCase().includes(text.toLowerCase())
      }),
    [vm.documentTypes],
  )
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource,
  })

  const pendingTask =
    vm.save.pending || vm.delete.pending || vm.fetchLinkedJobs.pending

  return (
    <div className={styles.documentDetails}>
      <Form onSubmit={vm.save}>
        <input type="submit" hidden disabled={pendingTask} />
        {vm.canEditTitleAndDescription && (
          <>
            <FormRow>
              <TextInputContainer
                label="Title"
                field="title"
                model={vm}
                placeholder={vm.title || 'Give your document a name'}
                error={vm.validation.getError('title')}
                readOnly={!vm.canEdit}
                disabled={!vm.canEdit || pendingTask}
              />
            </FormRow>
            <FormRow>
              <TextAreaContainer
                label="Description"
                maxLength={vm.descriptionLengthLimit}
                field="description"
                model={vm}
                rows={3}
                placeholder={'Optional description'}
                readOnly={!vm.canEdit}
                disabled={!vm.canEdit || pendingTask}
                help={`${vm.descriptionCharsRemaining} characters left`}
              />
            </FormRow>
          </>
        )}
        <FormRow>
          <FormGroup label="Type">
            <CustomSelect
              multi={false}
              {...selectProps}
              areItemsEqual={(left, right) =>
                (left && left.id) === (right && right.id)
              }
              value={vm.documentTypes.find((t) => t.id === vm.documentType?.id)}
              onChange={(t) => vm.setDocumentType(t ?? null)}
              itemToString={(item) => item.name}
              disabled={!vm.canChangedocumentType}
            />
          </FormGroup>
        </FormRow>
        {vm.save.rejected && (
          <ErrorBlock
            tackOn="bottom"
            error={vm.save.error}
            onCloseClick={() => vm.save.reset()}
          />
        )}
      </Form>
    </div>
  )
})
